import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';

import RequirePermission from '../components/AuthGuards/RequirePermission';
import { MODIFICATION_ENABLED } from '../api/modification/config';
import { NotAvailable } from '../pages/Modifications/components/ModificationNotices';
import { modificationRoutes } from '../constants/routes.constants';
import type { Permission } from '../constants/rbac';

const MyModificationsPage = lazy(() => import('../pages/Modifications/MyModificationsPage'));
const ModificationUploadPage = lazy(() => import('../pages/Modifications/ModificationUploadPage'));
const ModificationDetailPage = lazy(() => import('../pages/Modifications/ModificationDetailPage'));
const ReviewQueuePage = lazy(() => import('../pages/Modifications/ReviewQueuePage'));
const ReviewDetailPage = lazy(() => import('../pages/Modifications/ReviewDetailPage'));
const AdminModificationDetailPage = lazy(() => import('../pages/Modifications/AdminModificationDetailPage'));

/**
 * The flag decides whether the feature exists in this build; the permission
 * decides who may enter (both must pass, as for the upload routes). With
 * REACT_APP_MODIFICATION_MODE off, a direct visit says the feature is not
 * available and nothing is fetched or called. The flag is read at render.
 */
const Gate = ({ permission, children }: { permission: Permission; children: ReactNode }) =>
  MODIFICATION_ENABLED ? (
    <RequirePermission permission={permission}>
      <Suspense fallback={<></>}>{children}</Suspense>
    </RequirePermission>
  ) : (
    <NotAvailable />
  );

// The AI-modification screens (SOW Deliverable 4). Buyer screens behind
// submission.create, review screens behind submission.decide. The guards are a
// courtesy: the gateway enforces every route itself.
export const ModificationRoutes = [
  { path: modificationRoutes.mine, element: <Gate permission="submission.create"><MyModificationsPage /></Gate> },
  { path: modificationRoutes.newFor(':orderId'), element: <Gate permission="submission.create"><ModificationUploadPage /></Gate> },
  { path: modificationRoutes.upload(':id'), element: <Gate permission="submission.create"><ModificationUploadPage /></Gate> },
  { path: modificationRoutes.detail(':id'), element: <Gate permission="submission.create"><ModificationDetailPage /></Gate> },
  { path: modificationRoutes.review, element: <Gate permission="submission.decide"><ReviewQueuePage /></Gate> },
  { path: modificationRoutes.reviewDetail(':id'), element: <Gate permission="submission.decide"><ReviewDetailPage /></Gate> },
  // View only, behind the Admin tab's own key (Platform Admin full; a licensing admin, own tenant). E7 decides what it may see.
  { path: modificationRoutes.adminDetail(':id'), element: <Gate permission="submission.lock.reset"><AdminModificationDetailPage /></Gate> },
];
